// :: Dependencies
import {
  auth,
  firestore,
  firestoreDb,
  storageRef,
  functions,
  firebaseAuth,
  realtimeDb,
} from "@/config/firebase";
import { FirebaseError } from "@firebase/util";
import {
  USER_SAVE,
  USER_LOGIN,
  USER_LOGOUT,
  USER_PAY,
  SET_EVENT,
  SET_PARTICIPANT_EVENT_DATA,
  SET_PARTICIPANT,
  UPDATE_TICKET,
  SET_RESERVATIONS_ROOMS,
  SET_REGISTER_USER,
  SET_EVENTID,
  SET_PROGRAM,
  SET_STATUS,
  SET_BILLING_DATA,
  SET_COURSES,
  USER_COMPLETE_REGISTER_CARD,
  SET_BOOKINGS,
  SET_BILLINGS,
} from "./mutation-types";
import router from "@/config/router";
import handleLoginError from "@/services/HandleLoginErrors";
import { loaderStart, loaderDone } from "@/services/Loader";
import { PeopleEvent } from "@/vue-core/models/PeopleEvent.model";
import { TicketType } from "@/vue-core/models/TicketTypeEvent.model";
import { showMessage } from "@/services/MessageService";
import { i18n } from "@/i18n";
import { Hotel } from "@/vue-core/models/Hotel.model";
import { Room } from "@/vue-core/models/HotelRoom.model";
import { ProgramEvents } from "@/vue-core/models/ProgramEvents.model";
import { Charges } from "@/vue-core/models/Charges.model";
import store from "@/store";
import { Schedule } from "@/vue-core/models/Schedule.model";
import { Billing } from "@/vue-core/models/Billing.model";
import { Booking } from "@/vue-core/models/HotelBooking.model";

export default {
  /**
   * Signs a user with email and password
   * @param {Object} data The email and password data structure, Example: { email, password }
   */
  async passwordEmailAction(
    { dispatch, commit }: any,
    data: { email: string; password: string; eventID: string }
  ) {
    try {
      const { email, password, eventID } = data;
      const { user } = await auth.signInWithEmailAndPassword(email, password);
      if (user) {
        const idtokenResult = await user.getIdTokenResult();
        commit(USER_SAVE, {
          ...user,
          userClaims: idtokenResult.claims,
        });
        await dispatch("validLogin", { uid: user.uid, eventID });
        if (localStorage.getItem("afterLoginPath")) {
          const afterLoginPath = localStorage.getItem(
            "afterLoginPath"
          ) as string;
          router.push(afterLoginPath);
        } else {
          router.push({ name: "Home" });
        }
      }
    } catch (error: any) {
      if (error.code !== undefined) {
        handleLoginError(error.code);
      }
    }
  },
  /**
   * Signs a user in card template
   * @param {Object} data The email and password data structure, Example: { email, password }
   */
  async passwordEmailActionCard(
    { dispatch, commit }: any,
    data: { email: string; password: string; eventID: string }
  ) {
    try {
      const { email, password, eventID } = data;
      const { user } = await auth.signInWithEmailAndPassword(email, password);
      if (user) {
        const idtokenResult = await user.getIdTokenResult();
        commit(USER_SAVE, {
          ...user,
          userClaims: idtokenResult.claims,
        });
        await dispatch("validLogin", { uid: user.uid, eventID });
        if (localStorage.getItem("afterLoginPath")) {
          const afterLoginPath = localStorage.getItem(
            "afterLoginPath"
          ) as string;
          router.push(afterLoginPath);
        }
      }
    } catch (error: any) {
      if (error.code !== undefined) {
        handleLoginError(error.code);
      }
    }
  },
  /**
   * Login validation for possible registration status in card register
   */
  async validLoginInCardRegister(
    { dispatch, commit },
    {
      eventID,
      participantData,
      participantEventData,
    }: { eventID: string; participantData: any; participantEventData: any }
  ) {
    if (!participantData) return commit(USER_COMPLETE_REGISTER_CARD, true);
    if (!participantData!.exists)
      return commit(USER_COMPLETE_REGISTER_CARD, true);
    if (!participantEventData!.exists) {
      dispatch("validRegister", participantData?.data());
      await dispatch(
        "event/saveCardEventParticipantData",
        { ...participantData?.data(), eventID },
        { root: true }
      );
      return;
    }
    dispatch("validStatusPay", participantEventData.data());
    dispatch("validRegister", participantData?.data());
    dispatch("validRegisterByEvent", participantEventData?.data());
  },
  /**
   * Login validation for possible registration status
   */
  async validLogin(
    { dispatch, state },
    { uid, eventID }: { uid: string; eventID: string }
  ) {
    const participantData = await validParticipantRegister(uid);
    const participantEventData = await validParticipantEventRegister(
      eventID,
      uid
    );
    if (state.eventData) {
      const onlineRegister = state.eventData.eventFeatures.find(
        (el) => el.id === "online_registration"
      );
      if (onlineRegister && !onlineRegister.isSelected) {
        await dispatch("validLoginInCardRegister", {
          eventID,
          participantData,
          participantEventData,
        });
        return;
      }
    }
    // :: Go to complete Personal info
    if (!participantData) return router.push({ name: "UserRegister" });
    if (!participantData!.exists) return router.push({ name: "UserRegister" });
    // :: Go to complete event info
    if (!participantEventData!.exists) {
      dispatch("validRegister", participantData?.data());
      router.push({ name: "EventRegister" });
      return;
    }
    dispatch("validStatusPay", participantEventData.data());
    dispatch("validRegister", participantData?.data());
    dispatch("validRegisterByEvent", participantEventData?.data());
  },
  /**
   * Login with Facebook
   * @param eventID
   * @returns {void}
   */
  async facebookLoginAction({ dispatch, commit }, eventID: string) {
    try {
      const provider = new firebaseAuth.FacebookAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });
      const { user } = await auth.signInWithPopup(provider);
      if (user) {
        const idtokenResult = await user.getIdTokenResult();
        commit(USER_SAVE, {
          ...user,
          userClaims: idtokenResult.claims,
        });
        await dispatch("validLogin", { uid: user.uid, eventID });
        router.push({ name: "Home" });
      }
    } catch (error) {
      if (error instanceof FirebaseError) handleLoginError(error.code);
    }
  },
  /**
   * Login with Google
   * @param eventID
   * @returns {void}
   */
  async googleLoginAction({ dispatch, commit }, eventID: string) {
    try {
      const provider = new firebaseAuth.GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });
      const { user } = await auth.signInWithPopup(provider);
      if (user) {
        const idtokenResult = await user.getIdTokenResult();
        commit(USER_SAVE, {
          ...user,
          userClaims: idtokenResult.claims,
        });
        await dispatch("validLogin", { uid: user.uid, eventID });
        router.push({ name: "Home" });
      }
    } catch (error) {
      if (error instanceof FirebaseError) handleLoginError(error.code);
    }
  },
  async registerUser({ commit }, userData) {
    const { email, password } = userData;
    try {
      loaderStart();
      const userCredential = await auth.createUserWithEmailAndPassword(
        email,
        password
      );
      commit(USER_SAVE, userCredential.user);
      loaderDone();
      return userCredential.user;
    } catch (error: any) {
      handleLoginError(error.code);
      loaderDone();
    }
  },
  validRegister({ commit }: any, participantData) {
    commit(SET_REGISTER_USER, true);
    commit(SET_PARTICIPANT, participantData);
  },
  validRegisterByEvent({ commit }: any, participantData) {
    commit(SET_PARTICIPANT_EVENT_DATA, participantData);
    commit(USER_LOGIN, true);
  },
  resetValidRegister({ commit }: any) {
    commit(SET_REGISTER_USER, false);
    commit(SET_PARTICIPANT_EVENT_DATA, null);
    commit(USER_LOGIN, false);
    commit(USER_COMPLETE_REGISTER_CARD, false);
  },

  validStatusPay({ dispatch }, participantEventData) {
    if (participantEventData.status == "paid")
      dispatch("changeParticipantStatusPay", true);
    else dispatch("changeParticipantStatusPay", false);
  },
  changeParticipantStatusPay({ commit }: any, status) {
    commit(USER_PAY, status);
  },

  /**
   * Performs user logout
   * Documentation: https://firebase.google.com/docs/reference/js/firebase.auth.Auth#sign-out
   */
  async signOut({ commit, dispatch }: any) {
    try {
      await auth.signOut();
      commit(USER_LOGOUT);
      dispatch("changeParticipantStatusPay", false);
      router.push({ name: "Home" });
    } catch (error) {
      if (error instanceof FirebaseError) handleLoginError(error.code);
    }
  },
  /**
   * Adds an observer for changes to the user's sign-in state.
   * Documentation: https://firebase.google.com/docs/reference/js/firebase.auth.Auth#onauthstatechanged
   */
  getAuthState({ commit, state, dispatch }: any, eventId) {
    return new Promise((resolve, reject) => {
      auth.onAuthStateChanged(async function (user: any) {
        if (user) {
          const idtokenResult = await user.getIdTokenResult();
          commit(USER_SAVE, {
            ...user,
            userClaims: idtokenResult.claims,
          });
          if (Object.keys(state.data).length != 0) {
            await dispatch("validLogin", {
              uid: state.data.uid,
              eventID: eventId ? eventId : state.currentEventID,
            });
            resolve(user);
          }
        } else {
          commit(USER_LOGOUT);
          reject();
        }
      });
    });
  },

  /**
   * Send a password reset email
   * Documentation: https://firebase.google.com/docs/auth/web/manage-users#send_a_password_reset_email
   */
  async resetPasswordAction(
    { _ }: any,
    { email, eventID }: { email: string; eventID: string }
  ) {
    try {
      await auth.sendPasswordResetEmail(email, {
        url: `https://${process.env.VUE_APP_FIREBASE_AUTH_DOMAIN}/${eventID}`,
      });
    } catch (error) {
      if (error instanceof FirebaseError) handleLoginError(error.code);
    }
  },

  async getEventDataExists({ commit, dispatch, state }: any, eventID) {
    try {
      commit(SET_EVENTID, eventID);
      const eventDataSnapShot = await firestore("events").doc(eventID).get();
      if (eventDataSnapShot.exists) {
        const ticketTypesSnapShot = await firestore("events")
          .doc(eventID)
          .collection("tickets")
          .get();
        const hotelsSnapShot = await firestore("events")
          .doc(eventID)
          .collection("hotels")
          .get();
        const eventData = eventDataSnapShot.data() as PeopleEvent;
        eventData.ticketTypes = [];
        ticketTypesSnapShot.forEach((ticketSnapshot) => {
          const ticket = ticketSnapshot.data() as TicketType;
          ticket.id = ticketSnapshot.id;
          eventData.ticketTypes?.push(ticket);
        });
        eventData.hotels = [];
        for await (const hotelSnapShot of hotelsSnapShot.docs) {
          const hotel = hotelSnapShot.data() as Hotel;
          hotel.id = hotelSnapShot.id;
          const roomsSnapShot = await firestore("events")
            .doc(eventID)
            .collection("hotels")
            .doc(hotel.id)
            .collection("rooms")
            .get();
          hotel.rooms = [];
          for (const roomSnapShot of roomsSnapShot.docs) {
            const room = roomSnapShot.data() as Room;
            room.id = roomSnapShot.id;
            hotel.rooms?.push(room);
          }
          eventData.hotels?.push(hotel);
        }
        commit(SET_EVENT, {
          ...eventData,
          startDate: eventData.startDate.toDate(),
          endDate: eventData.endDate.toDate(),
        });
        if (Object.keys(state.data).length != 0) {
          await dispatch("resetValidRegister");
          await dispatch("validLogin", { uid: state.data.uid, eventID });
        }
        loaderDone();
        return eventData;
      } else {
        commit(SET_EVENT, null);
        router.push({ name: "NotFoundEvent" });
        loaderDone();
        return null;
      }
    } catch {
      loaderDone();
      return null;
    }
  },
  getFeaturesAccess({ state }, featureToSearch) {
    if (state.eventData.eventFeatures) {
      const featuresObject = state.eventData.eventFeatures;
      if (typeof featuresObject === "object") {
        const feature = featuresObject.find((el) => el.id === featureToSearch);
        if (!feature.isSelected) {
          router.push({ name: "Home" });
          return;
        }
      }
    } else {
      router.push({ name: "Home" });
    }
  },

  /**
   * Get hotel information
   * @param {Object} hotelData contains the eventID and hotelID to get hotel information
   * Used in HotelDetails view in /views/Hotels/HotelDetails.vue
   */
  async getHotelDetails({ _ }: any, hotelData): Promise<Hotel | null> {
    loaderStart();
    const { eventID, hotelID } = hotelData;
    try {
      const hotelSnapShot = await firestoreDb
        .collection("events")
        .doc(eventID)
        .collection("hotels")
        .doc(hotelID)
        .get();
      if (hotelSnapShot.exists) {
        const hotelDetails = hotelSnapShot.data() as Hotel;
        const roomsSnapShot = await firestoreDb
          .collection("events")
          .doc(eventID)
          .collection("hotels")
          .doc(hotelID)
          .collection("rooms")
          .get();
        hotelDetails.rooms = [];
        for await (const roomSnapShot of roomsSnapShot.docs) {
          const room = roomSnapShot.data() as Room;
          if (!room.lock) {
            room.id = roomSnapShot.id;
            hotelDetails.rooms?.push(room);
          }
        }
        loaderDone();
        return hotelDetails;
      } else {
        return null;
      }
    } catch (e) {
      showMessage({
        title: i18n.global.t("hotels.obtainHotelInfoError.title"),
        message: i18n.global.t("hotels.obtainHotelInfoError.message"),
        type: "error",
      });
      return null;
    }
  },

  /**
   * Get room information
   * @param {Object} data contains the eventID, hotelID and roomID to get room's information
   * Used in RoomDetails view in /views/Hotels/RoomDetails.vue
   */
  async roomDetailsData(_, data) {
    const { eventID, hotelID, roomID } = data;
    try {
      loaderStart();
      const roomsSnapShot = await firestoreDb
        .collection("events")
        .doc(eventID)
        .collection("hotels")
        .doc(hotelID)
        .collection("rooms")
        .doc(roomID)
        .get();
      const roomDetails = {
        ...roomsSnapShot.data(),
        startDate: roomsSnapShot.data()!.startDate.toDate(),
        endDate: roomsSnapShot.data()!.endDate.toDate(),
      };
      loaderDone();
      return roomDetails;
    } catch (e) {
      showMessage({
        title: i18n.global.t("hotels.rooms.obtainRoomInfoError.title"),
        message: i18n.global.t("hotels.rooms.obtainRoomInfoError.message"),
        type: "error",
      });
    }
  },
  /**
   * Update the new ticketType of the user
   * @param {Object} userData contains the eventID, uid and the new ticketType to update of the participant
   */
  async updateUserTicket({ commit }: any, userData) {
    try {
      await firestore("events")
        .doc(userData.eventID)
        .collection("participants")
        .doc(userData.uid)
        .update({ ticketType: userData.ticketType });
      commit(UPDATE_TICKET, userData.ticketType);
    } catch (error) {
      showMessage({
        title: i18n.global.t("register.selectTicketTypeError.title"),
        message: i18n.global.t("register.selectTicketTypeError.message"),
        type: "error",
      });
    }
  },
  /**
   * Update the new ticketType of the user
   * @param {Object} userData contains the userData to update
   */
  async updateUserData({ commit }: any, userData) {
    try {
      await firestore("events")
        .doc(userData.eventID)
        .collection("participants")
        .doc(userData.uid)
        .update({
          firstName: userData.firstName,
          lastName: userData.lastName,
          fullName: userData.fullName,
          phoneNumber: userData.phoneNumber,
          address: userData.address,
          description: userData.description,
        });
      await realtimeDb
        .ref(`shallow-participants/${userData.eventID}/${userData.uid}/`)
        .update({
          name: userData.fullName,
          uid: userData.uid,
        });
      commit(SET_PARTICIPANT_EVENT_DATA, userData);
    } catch (error) {
      showMessage({
        title: i18n.global.t("accountSettings.updateError.title"),
        message: i18n.global.t("accountSettings.updateError.message"),
        type: "warning",
      });
    }
  },
  /**
   * Update the new ticketType of the user
   * @param {Object} userData contains the userData profile image to update
   */
  async updateImageProfileUser({ commit }, userData) {
    try {
      const imageRef = storageRef.child(
        `events/${userData.eventID}/participants/${userData.uid}/profileImage`
      );
      await imageRef.putString(userData.profileImage, "data_url");
      const downloadProfileURL = await imageRef.getDownloadURL();
      await firestore("events")
        .doc(userData.eventID)
        .collection("participants")
        .doc(userData.uid)
        .update({
          profileImage: downloadProfileURL,
        });
      await firestore("participants").doc(userData.uid).update({
        profileImage: downloadProfileURL,
      });
      commit(SET_PARTICIPANT_EVENT_DATA, userData);
    } catch (e) {
      showMessage({
        title: i18n.global.t("accountSettings.updateImageError.title"),
        message: i18n.global.t("accountSettings.updateImageError.message"),
        type: "warning",
      });
    } finally {
      showMessage({
        title: i18n.global.t("accountSettings.updateImageSuccess.title"),
        message: i18n.global.t("accountSettings.updateImageSuccess.message"),
        type: "success",
      });
    }
  },
  /**
   * Set the reservation rooms
   * @param {Object} rooms contains the rooms info reservation
   * Used in  in HotelFilterList view in /views/Hotels/HotelFilterList.vue
   */
  async reservationRooms({ commit }, rooms) {
    commit(SET_RESERVATIONS_ROOMS, { ...rooms });
  },
  /**
   * Get hotel information
   * @param {Object} hotelData contains the eventID and hotelID to get hotel information
   * Used in ReservationDetails view in /views/Hotels/ReservationDetails.vue
   */ async getHotelData({ _ }: any, hotelData): Promise<Hotel | null> {
    loaderStart();
    const { eventID, hotelID } = hotelData;
    try {
      const hotelSnapShot = await firestoreDb
        .collection("events")
        .doc(eventID)
        .collection("hotels")
        .doc(hotelID)
        .get();
      if (hotelSnapShot.exists) {
        const hotelData = hotelSnapShot.data() as Hotel;
        return hotelData;
      } else {
        return null;
      }
    } catch (e) {
      showMessage({
        title: i18n.global.t("hotels.obtainHotelInfoError.title"),
        message: i18n.global.t("hotels.obtainHotelInfoError.message"),
        type: "error",
      });
      return null;
    }
  },
  async getHotelCharges({ commit }, dataCharges) {
    const { hotelID, eventID } = dataCharges;
    try {
      const chargesList: Array<Charges> = [];
      const charges = await firestoreDb
        .collection("events")
        .doc(eventID)
        .collection("hotels")
        .doc(hotelID)
        .collection("extraCharges")
        .get();
      for (const item of charges.docs) {
        const charge = item.data() as Charges;
        chargesList.push(charge);
      }
      return chargesList;
    } catch (error) {
      showMessage({
        title: i18n.global.t("hotels.errorAddBooking.title"),
        message: i18n.global.t("hotels.errorAddBooking.message"),
        type: "error",
      });
    }
  },
  /**
   * Add reservation
   * @param {Object} dataReservation contains the data of the reservation
   * Used in  in HotelFileterList view in views\Hotels\HotelFileterList.vue
   */
  async addReservation(
    { dispatch },
    { hotelID, eventID, rooms, adultSelected, childSelected, dates }
  ) {
    const people = parseInt(adultSelected) + parseInt(childSelected);
    try {
      const fromDate = new Date(dates.fromDate as string);
      const toDate = new Date(dates.toDate as string);
      const timeDiff = Math.abs(fromDate.getTime() - toDate.getTime());
      const quantity = Math.ceil(timeDiff / (1000 * 3600 * 24));
      const dataCharges = await dispatch("getHotelCharges", {
        eventID,
        hotelID,
      });

      const clearCart = functions.httpsCallable("clearShoppingCart");
      await clearCart({ eventId: eventID });
      const addProduct = functions.httpsCallable("addProduct");
      for (const room of rooms) {
        await addProduct({
          eventId: eventID,
          productDocumentPath: `events/${eventID}/hotels/${hotelID}/rooms/${room.id}/`,
          quantity: quantity,
        });
      }
      if (dataCharges.length > 0) {
        for (const charge of dataCharges) {
          if (charge.name === "concierge") {
            await addProduct({
              eventId: eventID,
              productDocumentPath: `events/${eventID}/hotels/${hotelID}/extraCharges/${charge.id}`,
              quantity: quantity * rooms.length,
            });
          } else {
            await addProduct({
              eventId: eventID,
              productDocumentPath: `events/${eventID}/hotels/${hotelID}/extraCharges/${charge.id}`,
              quantity: people,
            });
          }
        }
      }
      await router.push({
        name: "BookingsCheckout",
        params: { eventID: eventID },
        query: {
          adultSelected: adultSelected,
          childSelected: childSelected,
          toDate: dates.toDate,
          fromDate: dates.fromDate,
          rooms: rooms,
        },
      });
    } catch {
      showMessage({
        title: i18n.global.t("hotels.errorAddBooking.title"),
        message: i18n.global.t("hotels.errorAddBooking.message"),
        type: "error",
      });
    }
  },
  /**
   * Update reservation
   * @param {Object} dataReservation contains the data of the reservation to update
   * Used in  in BookingsPayment view in views\Hotels\BookingsPayment.vue
   */
  async updateReservationParticipant({ commit }, dataReservation) {
    try {
      const hotelID = dataReservation.hotelID;
      const eventID = dataReservation.eventID;
      const rooms = dataReservation.rooms;
      const coupon = dataReservation.coupon;
      const dataCharges = dataReservation.charges;
      const sizeRooms = dataReservation.rooms.length;
      const clearCart = functions.httpsCallable("clearShoppingCart");
      await clearCart({ eventId: eventID });
      const addProduct = functions.httpsCallable("addProduct");
      for (const room of rooms) {
        await addProduct({
          eventId: eventID,
          productDocumentPath: `events/${eventID}/hotels/${hotelID}/rooms/${room.product.id}`,
          quantity: room.quantity,
          coupon: coupon,
        });
      }
      if (dataCharges.length > 0) {
        for (const charge of dataCharges) {
          if (charge.name === "concierge") {
            await addProduct({
              eventId: eventID,
              productDocumentPath: `events/${eventID}/hotels/${hotelID}/extraCharges/${charge.product.id}`,
              quantity: charge.quantity,
              coupon: coupon,
            });
          } else {
            await addProduct({
              eventId: eventID,
              productDocumentPath: `events/${eventID}/hotels/${hotelID}/extraCharges/${charge.product.id}`,
              quantity: charge.quantity,
              coupon: coupon,
            });
          }
        }
      }
    } catch (error) {
      showMessage({
        title: i18n.global.t("hotels.errorAddBooking.title"),
        message: i18n.global.t("hotels.errorAddBooking.message"),
        type: "error",
      });
    }
  },
  /**
   * Update reservation
   * @param {Object} dataBooking contains the data of the booking to save
   * Used in  in BookingsPayment view in views\Hotels\BookingsPayment.vue
   */
  async saveBooking({ commit }, dataBooking) {
    loaderStart();
    try {
      const participantID = store.getters["user/participantData"].uid;
      const eventID = store.getters["user/eventData"].id;
      const booking = dataBooking.booking;
      const cartID = dataBooking.cartID;
      const fromDate = dataBooking.fromDate;
      const toDate = dataBooking.toDate;
      const couponID = dataBooking.couponID;

      const stripeUpdateCouponRedemptions = functions.httpsCallable(
        "stripeUpdateCouponRedemptions"
      );
      const validRedemptions = await stripeUpdateCouponRedemptions({
        coupongId: couponID,
        eventId: eventID,
        coupongType: "booking",
      });
      if (validRedemptions.data === "valid") {
        const preBook = functions.httpsCallable("preBook");
        await preBook({
          eventID: eventID,
          participantID: participantID,
          booking: booking,
          cartID: cartID,
          fromDate: fromDate,
          toDate: toDate,
          priceIsZero: true,
        });
        await store.dispatch("event/counterStatisticsBookings", eventID);
        showMessage({
          title: i18n.global.t("register.paymentSuccess.title"),
          message: i18n.global.t("register.paymentSuccess.message"),
          type: "success",
        });
      } else {
        showMessage({
          title: i18n.global.t("coupon.invalidCoupon"),
          message: i18n.global.t("coupon.invalidCouponDetail"),
          type: "error",
        });
      }
    } catch (error) {
      showMessage({
        title: i18n.global.t("hotels.errorSaveBooking.title"),
        message: i18n.global.t("hotels.errorSaveBooking.message"),
        type: "error",
      });
    } finally {
      loaderDone();
      router.push({
        name: "MyBookings",
      });
    }
  },
  async getProgramPreview({ commit }: any, eventID) {
    try {
      const result: Array<ProgramEvents> = [];
      const mainSpeakers: Array<ProgramEvents> = [];
      const querySnapshot = await firestore("events")
        .doc(eventID)
        .collection("program")
        .orderBy("scheduledDates", "asc")
        .get();
      querySnapshot.forEach((doc) => {
        result.push({ ...(doc.data() as ProgramEvents) });
      });
      let cont = 0;
      result.forEach((event) => {
        if (event.speaker) {
          if (event.speaker.mainSpeaker && event.scheduledDates) {
            cont++;
            mainSpeakers.push(event);
          }
        }
      });
      commit(SET_PROGRAM, mainSpeakers);
      commit(SET_STATUS, cont > 0 ? true : false);
    } catch (error) {
      showMessage({
        title: i18n.global.t("program.messages.getProgramErrorTitle"),
        message: i18n.global.t("program.messages.getProgramErrorMessage"),
        type: "error",
      });
    } finally {
      loaderDone();
    }
  },
  async getProgramData({ _ }: any, data) {
    const { eventID } = data;
    try {
      loaderStart();
      const programSnapShot = await firestore("events")
        .doc(eventID)
        .collection("program")
        .get();
      const programDateMap: { [key: string]: Array<any> } = {};
      for (const program of programSnapShot.docs) {
        if (program.data().scheduledDates) {
          for (const dateProgram of program.data().scheduledDates) {
            const { formatDate, startTime, endTime, dateStart, dateEnd } =
              getDates({
                startDate: dateProgram.startDate.toDate(),
                endDate: dateProgram.endDate.toDate(),
                durationInMilliseconds: dateProgram.durationInMilliseconds,
              });
            const date = new Date(formatDate);
            if (programDateMap[date.getTime()])
              programDateMap[date.getTime()].push({
                ...program.data(),
                startTime,
                endTime,
                dateStart,
                dateEnd,
              });
            else
              programDateMap[date.getTime()] = [
                { ...program.data(), startTime, endTime, dateStart, dateEnd },
              ];
          }
        }
      }
      loaderDone();
      const orderArray = Object.entries(programDateMap).sort();
      return Object.fromEntries(orderArray);
    } catch (e) {
      loaderDone();
      showMessage({
        title: i18n.global.t("program.messages.getProgramErrorTitle"),
        message: i18n.global.t("program.messages.getProgramErrorMessage"),
        type: "error",
      });
    }
  },

  /**
   * Get Courses
   * @param {Object} participantData contains the eventID, couponID participantID and courseID to register a course with price 0
   * Used in CoursePayment view in src\views\Program\CoursePayment.vue
   */

  async getCourses({ commit, state }: any) {
    loaderStart();
    try {
      const eventID = store.getters["user/eventData"].id;
      const participantID = state.data.uid;
      const coursesSnap = await firestoreDb
        .collection("events")
        .doc(eventID)
        .collection("participants")
        .doc(participantID)
        .collection("courses")
        .onSnapshot((coursesRef) => {
          const courses: ProgramEvents[] = [];
          coursesRef.forEach((doc) => {
            courses.push({
              ...doc.data(),
            } as ProgramEvents);
          });
          commit(SET_COURSES, courses);
        });
    } catch (error) {
      showMessage({
        title: i18n.global.t("program.registerCourse.registerError.title"),
        message: i18n.global.t("program.registerCourse.registerError.message"),
        type: "error",
      });
    } finally {
      loaderDone();
    }
  },
  setLenguage({ state }) {
    if (state.eventData.language) i18n.global.locale = state.eventData.language;
  },
  /**
   * Check if the participant has an entrance record in the event
   */
  async checkEventEntranceRecord({ commit, state }: any) {
    try {
      const eventID = store.getters["user/eventData"].id;
      const isUserElegibleToGetCertificate = functions.httpsCallable(
        "isUserElegibleToGetCertificate"
      );
      const result = await isUserElegibleToGetCertificate({
        eventId: eventID,
      });
      return result.data.canGetCertificate;
    } catch (error) {
      showMessage({
        title: i18n.global.t(
          "program.courses.errorCheckEntranceEventMessage.title"
        ),
        message: i18n.global.t(
          "program.courses.errorCheckEntranceEventMessage.message"
        ),
        type: "error",
      });
      return false;
    }
  },
  async getBillings({ state, commit }: any) {
    try {
      const eventID = store.getters["user/eventData"].id;
      const participantID = state.data.uid;
      await firestore("events")
        .doc(eventID)
        .collection("participants")
        .doc(participantID)
        .collection("billing")
        .onSnapshot((billingsRef) => {
          const billings: Array<Billing> = [];
          billingsRef.forEach((booking) => {
            billings.push(booking.data() as Billing);
          });
          commit(SET_BILLINGS, billings);
        });
    } catch (error) {
      showMessage({
        title: i18n.global.t("billing.messages.error"),
        message: i18n.global.t("billing.messages.errorGet"),
        type: "error",
      });
    }
  },
  saveLocalBillingData({ commit }, data) {
    commit(SET_BILLING_DATA, data);
  },
  async generateInvoice({ state }: any, billingData: Billing) {
    try {
      const eventID = store.getters["user/eventData"].id;
      const eventName = store.getters["user/eventData"].name;
      const orgId = store.getters["user/eventData"].orgOwnerId;
      const participantID = state.data.uid;
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() - 1);
      billingData.eventName = eventName;
      billingData.requestDate = currentDate.toISOString();
      billingData.uidRequest = participantID;

      console.info("generateInvoice", "billingData", billingData);
      await firestoreDb
        .collection("events")
        .doc(eventID)
        .collection("participants")
        .doc(participantID)
        .collection("billing")
        .doc(billingData.id)
        .update(billingData);
      console.info("generateInvoice", " billing saved");
      await firestoreDb
        .collection("organizations")
        .doc(orgId)
        .collection("billings")
        .doc(billingData.id)
        .delete();
      console.info("generateInvoice", " delete org billing doc");
      const stampReceiptBilling = functions.httpsCallable(
        "autoStampReceiptBilling"
      );
      await stampReceiptBilling(billingData);

      console.info("generateInvoice", "stampReceiptBilling");
      showMessage({
        title: i18n.global.t("billing.messages.successRequest"),
        message: i18n.global.t("billing.messages.successRequestMS"),
        type: "success",
      });
      return true;
    } catch (error) {
      console.error("ERROR FACT,", error, billingData);
      showMessage({
        title: i18n.global.t("billing.messages.error"),
        message: i18n.global.t("billing.messages.errorInvoice"),
        type: "error",
      });
      return false;
    }
  },
  async updateTicketParticipant({ _ }: any, userData) {
    try {
      const clearCart = functions.httpsCallable("clearShoppingCart");
      await clearCart({ eventId: userData.eventID });
      const addProduct = functions.httpsCallable("addProduct");
      await addProduct({
        eventId: userData.eventID,
        productDocumentPath: `events/${userData.eventID}/tickets/${userData.ticketType}`,
        quantity: 1,
      });
    } catch (error) {
      showMessage({
        title: i18n.global.t("register.registerError.title"),
        message: i18n.global.t("register.registerError.message"),
        type: "error",
      });
    }
  },
  changeCompleRegisterCard({ commit }, value) {
    commit(USER_COMPLETE_REGISTER_CARD, value);
  },
  async getBookings({ commit, state }: any) {
    try {
      const eventID = store.getters["user/eventData"].id;
      const uid = state.data.uid;

      await firestore("events")
        .doc(eventID)
        .collection("participants")
        .doc(uid)
        .collection("bookings")
        .onSnapshot((bookingsRef) => {
          const bookings: Array<Booking> = [];
          bookingsRef.forEach((booking) => {
            bookings.push({
              ...(booking.data() as Booking),
              checkIn: booking.data().checkIn.toDate(),
              checkOut: booking.data().checkOut.toDate(),
            });
          });
          commit(SET_BOOKINGS, bookings);
        });
    } catch (error) {
      showMessage({
        title: i18n.global.t("register.registerError.title"),
        message: i18n.global.t("register.registerError.message"),
        type: "error",
      });
    }
  },
  async getBillingInvoice({ commit }: any, path: string) {
    try {
      return await storageRef.child(path).getDownloadURL();
    } catch (error) {
      showMessage({
        title: i18n.global.t("register.registerError.title"),
        message: i18n.global.t("register.registerError.message"),
        type: "error",
      });
    }
  },
};

async function validParticipantRegister(uid) {
  const docParticipant = await firestore("participants").doc(uid).get();
  return docParticipant;
}
async function validParticipantEventRegister(eventID, uid) {
  const docParticipant = await firestore("events")
    .doc(eventID)
    .collection("participants")
    .doc(uid)
    .get();
  return docParticipant;
}

function getDates(scheduledDate: Schedule) {
  const options: any = { day: "2-digit", month: "short", year: "numeric" };
  const formatDate = scheduledDate.startDate.toLocaleDateString(
    "en-GB",
    options
  );
  const startTime = convertHours(scheduledDate.startDate);
  const endTime = convertHours(scheduledDate.endDate);
  const dateStart = scheduledDate.startDate;
  const dateEnd = scheduledDate.endDate;
  return { formatDate, startTime, endTime, dateStart, dateEnd };
}
function convertHours(date: Date) {
  let hours: string | number = date.getHours();
  let minutes: string | number = date.getMinutes();
  let seconds: string | number = date.getSeconds();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12;
  hours = hours < 10 ? "0" + hours : hours;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  seconds = seconds < 10 ? "0" + seconds : seconds;
  const strTime = hours + ":" + minutes + " " + ampm;
  return strTime;
}
